/**
 * 卖家中心合规状态枚举（图1 实拍图识别状态 / 图2 商品合规信息状态）
 */
import { fixedUi } from '@/i18n/fixedUi';
import i18n from '@/i18n';

/** 商品合规信息 · 合规类型（metadata.complianceType） */
export const COMPLIANCE_TYPE_OPTIONS = [
  { value: '', label: '全部' },
  { value: '1', label: '欧盟负责人', labelKey: 'compliance.ct1' },
  { value: '2', label: '美代责任人', labelKey: 'compliance.ct2' },
  { value: '3', label: '制造商信息', labelKey: 'compliance.ct3' },
  { value: '4', label: '加州65号提案', labelKey: 'compliance.ct4' },
  { value: '5', label: '其他合规信息', labelKey: 'compliance.ct5' },
];

/** 常见「合规信息」名称（与类型名对齐，可手输其它） */
export const COMPLIANCE_INFO_NAME_OPTIONS = [
  { value: '', label: '全部' },
  { value: '欧盟负责人', label: '欧盟负责人', labelKey: 'compliance.ct1' },
  { value: '美代责任人', label: '美代责任人', labelKey: 'compliance.ct2' },
  { value: '制造商信息', label: '制造商信息', labelKey: 'compliance.ct3' },
  { value: '加州65号提案', label: '加州65号提案', labelKey: 'compliance.ct4' },
  { value: '其他合规信息', label: '其他合规信息', labelKey: 'compliance.ct5' },
];

/** 商品合规信息 · 状态 */
export const INFO_STATUS = {
  WAIT_UPLOAD: 'wait_upload',
  SUCCESS: 'success',
  PENDING_CONFIRM: 'pending_confirm',
  UPLOADING: 'uploading',
  FAILED: 'failed',
};

export const INFO_STATUS_TABS = [
  { id: 'all', label: '全部' },
  { id: INFO_STATUS.WAIT_UPLOAD, label: '待上传' },
  { id: INFO_STATUS.SUCCESS, label: '上传成功' },
  { id: INFO_STATUS.PENDING_CONFIRM, label: '待确认' },
  { id: INFO_STATUS.UPLOADING, label: '上传中' },
  { id: INFO_STATUS.FAILED, label: '上传失败' },
];

const INFO_LABELS = {
  [INFO_STATUS.WAIT_UPLOAD]: '待上传',
  [INFO_STATUS.SUCCESS]: '上传成功',
  [INFO_STATUS.PENDING_CONFIRM]: '待确认',
  [INFO_STATUS.UPLOADING]: '上传中',
  [INFO_STATUS.FAILED]: '上传失败',
};

const INFO_TONES = {
  [INFO_STATUS.WAIT_UPLOAD]: 'bad',
  [INFO_STATUS.SUCCESS]: 'ok',
  [INFO_STATUS.PENDING_CONFIRM]: 'pending',
  [INFO_STATUS.UPLOADING]: 'pending',
  [INFO_STATUS.FAILED]: 'bad',
};

/** 实拍图 · 识别状态 */
export const PHOTO_STATUS = {
  WAIT_UPLOAD: 'wait_upload',
  CAPABILITY_BUILDING: 'capability_building',
  DEEP_RECOGNIZING: 'deep_recognizing',
  LABEL_ABNORMAL: 'label_abnormal',
  SUCCESS: 'success',
  NON_STICKABLE: 'non_stickable',
  WAREHOUSE_NONCOMPLIANT: 'warehouse_noncompliant',
};

export const PHOTO_STATUS_TABS = [
  { id: 'all', label: '全部' },
  { id: PHOTO_STATUS.WAIT_UPLOAD, label: '待传图' },
  { id: PHOTO_STATUS.CAPABILITY_BUILDING, label: '系统识别能力待建设' },
  { id: PHOTO_STATUS.DEEP_RECOGNIZING, label: '深度识别中' },
  { id: PHOTO_STATUS.LABEL_ABNORMAL, label: '图中标签有异常' },
  { id: PHOTO_STATUS.SUCCESS, label: '识别成功' },
  { id: PHOTO_STATUS.NON_STICKABLE, label: '含有不可贴的标签' },
  { id: PHOTO_STATUS.WAREHOUSE_NONCOMPLIANT, label: '仓库实收商品不合规' },
];

const PHOTO_LABELS = {
  [PHOTO_STATUS.WAIT_UPLOAD]: '待传图',
  [PHOTO_STATUS.CAPABILITY_BUILDING]: '系统识别能力待建设',
  [PHOTO_STATUS.DEEP_RECOGNIZING]: '深度识别中',
  [PHOTO_STATUS.LABEL_ABNORMAL]: '图中标签有异常',
  [PHOTO_STATUS.SUCCESS]: '识别成功',
  [PHOTO_STATUS.NON_STICKABLE]: '含有不可贴的标签',
  [PHOTO_STATUS.WAREHOUSE_NONCOMPLIANT]: '仓库实收商品不合规',
};

const PHOTO_TONES = {
  [PHOTO_STATUS.WAIT_UPLOAD]: 'failed',
  [PHOTO_STATUS.CAPABILITY_BUILDING]: 'pending',
  [PHOTO_STATUS.DEEP_RECOGNIZING]: 'pending',
  [PHOTO_STATUS.LABEL_ABNORMAL]: 'failed',
  [PHOTO_STATUS.SUCCESS]: 'success',
  [PHOTO_STATUS.NON_STICKABLE]: 'failed',
  [PHOTO_STATUS.WAREHOUSE_NONCOMPLIANT]: 'failed',
};

/** 兼容旧键 → 新识别状态 */
const PHOTO_ALIASES = {
  pending: PHOTO_STATUS.WAIT_UPLOAD,
  todo: PHOTO_STATUS.WAIT_UPLOAD,
  reviewing: PHOTO_STATUS.DEEP_RECOGNIZING,
  review: PHOTO_STATUS.DEEP_RECOGNIZING,
  approved: PHOTO_STATUS.SUCCESS,
  done: PHOTO_STATUS.SUCCESS,
  rejected: PHOTO_STATUS.LABEL_ABNORMAL,
  failed: PHOTO_STATUS.LABEL_ABNORMAL,
};

/** 兼容旧键 → 新合规信息状态 */
const INFO_ALIASES = {
  pending: INFO_STATUS.PENDING_CONFIRM,
  unbound: INFO_STATUS.WAIT_UPLOAD,
  todo: INFO_STATUS.WAIT_UPLOAD,
  done: INFO_STATUS.SUCCESS,
  bound: INFO_STATUS.SUCCESS,
  approved: INFO_STATUS.SUCCESS,
};

function normalizeToken(raw) {
  return String(raw == null ? '' : raw).trim().toLowerCase();
}

export function normalizeInfoStatus(raw) {
  const v = normalizeToken(raw);
  if (!v || v === 'all' || v === 'unknown') return '';
  if (INFO_LABELS[v]) return v;
  if (INFO_ALIASES[v]) return INFO_ALIASES[v];
  if (v.includes('待上传') || v.includes('wait_upload') || v.includes('to_upload')) {
    return INFO_STATUS.WAIT_UPLOAD;
  }
  if (v.includes('上传成功') || v.includes('success') || v.includes('uploaded')) {
    return INFO_STATUS.SUCCESS;
  }
  if (v.includes('待确认') || v.includes('confirm')) return INFO_STATUS.PENDING_CONFIRM;
  if (v.includes(fixedUi("上传中")) || v.includes('uploading')) return INFO_STATUS.UPLOADING;
  if (v.includes(fixedUi("上传失败")) || v.includes('fail')) return INFO_STATUS.FAILED;
  const n = Number(raw);
  if (Number.isFinite(n)) {
    // metadata.complianceInfoList.status：对齐卖家中心筛选项顺序
    if (n === 0) return INFO_STATUS.WAIT_UPLOAD;
    if (n === 1) return INFO_STATUS.SUCCESS;
    if (n === 2) return INFO_STATUS.PENDING_CONFIRM;
    if (n === 3) return INFO_STATUS.UPLOADING;
    if (n === 4) return INFO_STATUS.FAILED;
  }
  return '';
}

export function infoStatusDisplay(raw) {
  const code = normalizeInfoStatus(raw);
  if (!code) return { code: '', label: '—', tone: 'muted' };
  const fallback = INFO_LABELS[code] || code;
  return {
    code,
    label: i18n.t('compliance.info_' + code, { ns: 'workbench', defaultValue: fallback }),
    tone: INFO_TONES[code] || 'muted',
  };
}

export function normalizePhotoStatus(raw) {
  const v = normalizeToken(raw);
  if (!v || v === 'all' || v === 'unknown') return '';
  if (PHOTO_LABELS[v]) return v;
  if (PHOTO_ALIASES[v]) return PHOTO_ALIASES[v];
  if (v.includes('待传图') || v.includes('wait_upload') || v.includes('to_upload')) {
    return PHOTO_STATUS.WAIT_UPLOAD;
  }
  if (v.includes('系统识别能力') || v.includes('capability')) {
    return PHOTO_STATUS.CAPABILITY_BUILDING;
  }
  if (v.includes('深度识别') || v.includes('deep')) return PHOTO_STATUS.DEEP_RECOGNIZING;
  if (v.includes('标签有异常') || v.includes('abnormal') || v.includes('异常')) {
    return PHOTO_STATUS.LABEL_ABNORMAL;
  }
  if (v.includes('识别成功') || v.includes('success') || v.includes('approved')) {
    return PHOTO_STATUS.SUCCESS;
  }
  if (v.includes('不可贴') || v.includes('non_stick') || v.includes('unstick')) {
    return PHOTO_STATUS.NON_STICKABLE;
  }
  if (v.includes('仓库实收') || v.includes('warehouse')) {
    return PHOTO_STATUS.WAREHOUSE_NONCOMPLIANT;
  }
  const n = Number(raw);
  if (Number.isFinite(n)) {
    // bg.flash.open.upload.recognize → ruleCheckResult[].ruleStatus
    if (n === 1) return PHOTO_STATUS.WAIT_UPLOAD;
    if (n === 2) return PHOTO_STATUS.SUCCESS;
    if (n === 3) return PHOTO_STATUS.CAPABILITY_BUILDING;
    if (n === 4) return PHOTO_STATUS.LABEL_ABNORMAL;
    if (n === 5) return PHOTO_STATUS.DEEP_RECOGNIZING;
    if (n === 6) return PHOTO_STATUS.NON_STICKABLE;
    if (n === 7) return PHOTO_STATUS.WAREHOUSE_NONCOMPLIANT;
    if (n === 0) return PHOTO_STATUS.WAIT_UPLOAD;
  }
  return '';
}

export function photoStatusDisplay(raw) {
  const code = normalizePhotoStatus(raw);
  if (!code) return { code: '', label: '—', tone: 'pending' };
  const fallback = PHOTO_LABELS[code] || code;
  return {
    code,
    label: i18n.t('compliance.ph_' + code, { ns: 'workbench', defaultValue: fallback }),
    tone: PHOTO_TONES[code] || 'pending',
  };
}

export function emptyPhotoDimensionStats() {
  const stats = { all: 0 };
  PHOTO_STATUS_TABS.forEach((tab) => {
    if (tab.id !== 'all') stats[tab.id] = 0;
  });
  return stats;
}

export function emptyInfoDimensionStats() {
  const stats = { all: 0 };
  INFO_STATUS_TABS.forEach((tab) => {
    if (tab.id !== 'all') stats[tab.id] = 0;
  });
  return stats;
}
